import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Search, Minus, Plus, CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { http, dateLabel, ymd, CABINS } from "../lib/api";
import { toast } from "sonner";

const AirportField = ({ label, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => http.get("/airports", { params: { q } }).then((r) => setList(r.data)), 200);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`${testid}-trigger`} className="text-left w-full rounded-xl border border-slate-200 hover:border-[#EC5B24] px-4 py-3 transition-colors bg-white">
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="font-heading text-2xl font-bold text-slate-900 leading-tight">{value?.code}</div>
          <div className="text-xs text-slate-500 truncate">{value?.city}{value?.name ? `, ${value.name}` : ""}</div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city or airport"
          data-testid={`${testid}-input`} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#EC5B24]" />
        <div className="mt-2 max-h-72 overflow-y-auto">
          {list.map((a) => (
            <button key={a.code} data-testid={`${testid}-option-${a.code}`} onClick={() => { onChange(a); setOpen(false); setQ(""); }}
              className="w-full flex items-center gap-3 text-left px-2 py-2 rounded-lg hover:bg-orange-50">
              <span className="font-mono text-xs font-bold bg-slate-100 rounded px-1.5 py-1 w-11 text-center">{a.code}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800">{a.city}, {a.country}</span>
                <span className="block text-xs text-slate-500 truncate">{a.name}</span>
              </span>
            </button>
          ))}
          {!list.length && /^[a-zA-Z]{3}$/.test(q.trim()) ? (
            <button data-testid={`${testid}-custom-code`} onClick={() => { const c = q.trim().toUpperCase(); onChange({ code: c, city: c, name: "", country: "" }); setOpen(false); setQ(""); }}
              className="w-full text-left px-2 py-2 rounded-lg hover:bg-orange-50 text-sm">Use airport code <b className="font-mono">{q.trim().toUpperCase()}</b></button>
          ) : !list.length && <div className="text-sm text-slate-500 p-3">No airports found</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DateField = ({ label, value, onChange, min, testid, onClear, placeholder }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`${testid}-trigger`} className="relative text-left w-full rounded-xl border border-slate-200 hover:border-[#EC5B24] px-4 py-3 transition-colors bg-white">
          <div className="text-xs font-medium text-slate-500 flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {label}</div>
          {value ? (
            <>
              <div className="font-heading text-lg font-bold text-slate-900">{dateLabel(value, { day: "numeric", month: "short" })}</div>
              <div className="text-xs text-slate-500">{dateLabel(value, { weekday: "long", year: "numeric" })}</div>
            </>
          ) : (
            <div className="text-sm text-slate-400 py-2">{placeholder}</div>
          )}
          {value && onClear && (
            <span role="button" data-testid={`${testid}-clear`} onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="absolute right-2 top-2 p-1 rounded-full hover:bg-slate-100"><X className="h-3 w-3" /></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value ? new Date(value + "T00:00:00") : undefined}
          disabled={(d) => d < new Date(min + "T00:00:00")}
          onSelect={(d) => { if (d) { onChange(ymd(d)); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

const Counter = ({ label, sub, value, set, min, max, testid }) => (
  <div className="flex items-center justify-between py-2">
    <div><div className="text-sm font-semibold">{label}</div><div className="text-xs text-slate-500">{sub}</div></div>
    <div className="flex items-center gap-3">
      <button data-testid={`${testid}-minus`} disabled={value <= min} onClick={() => set(value - 1)} className="h-7 w-7 rounded-full border grid place-items-center disabled:opacity-30"><Minus className="h-3 w-3" /></button>
      <span className="w-4 text-center font-semibold" data-testid={`${testid}-value`}>{value}</span>
      <button data-testid={`${testid}-plus`} disabled={value >= max} onClick={() => set(value + 1)} className="h-7 w-7 rounded-full border grid place-items-center disabled:opacity-30"><Plus className="h-3 w-3" /></button>
    </div>
  </div>
);

const DEFAULT_FROM = { code: "DEL", city: "New Delhi", name: "Indira Gandhi International Airport", country: "India" };
const DEFAULT_TO = { code: "BOM", city: "Mumbai", name: "Chhatrapati Shivaji Maharaj International Airport", country: "India" };

export const SearchWidget = ({ initial, compact }) => {
  const navigate = useNavigate();
  const today = ymd(new Date());
  const [trip, setTrip] = useState(initial?.ret ? "round" : "oneway");
  const [from, setFrom] = useState(initial?.from || DEFAULT_FROM);
  const [to, setTo] = useState(initial?.to || DEFAULT_TO);
  const [depart, setDepart] = useState(initial?.date || ymd(new Date(Date.now() + 7 * 864e5)));
  const [ret, setRet] = useState(initial?.ret || "");
  const [pax, setPax] = useState(initial?.pax || { adults: 1, children: 0, infants: 0 });
  const [cabin, setCabin] = useState(initial?.cabin || "economy");
  const [spin, setSpin] = useState(false);

  const total = pax.adults + pax.children + pax.infants;
  const swap = () => { setSpin((s) => !s); setFrom(to); setTo(from); };

  const submit = () => {
    if (from.code === to.code) return toast.error("Origin and destination cannot be the same");
    if (trip === "round" && !ret) return toast.error("Please select a return date");
    const p = new URLSearchParams({ from: from.code, to: to.code, date: depart, adults: pax.adults, children: pax.children, infants: pax.infants, cabin });
    if (trip === "round") p.set("ret", ret);
    p.set("fromCity", from.city); p.set("toCity", to.city);
    navigate(`/flights?${p.toString()}`);
  };

  return (
    <div className={`bg-white rounded-2xl ${compact ? "p-4 border border-slate-200" : "p-5 sm:p-6 shadow-[0_20px_60px_-20px_rgba(28,26,74,0.35)]"}`} data-testid="flight-search-widget">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[["oneway", "One Way"], ["round", "Round Trip"]].map(([id, l]) => (
          <button key={id} data-testid={`trip-type-${id}`} onClick={() => { setTrip(id); if (id === "oneway") setRet(""); }}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${trip === id ? "bg-[#1C1A4A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
        <span className="ml-auto text-xs font-semibold text-[#0F9D58] bg-[#E8F5E9] rounded-full px-3 py-1">Live fares · UPI payments</span>
      </div>
      <div className="grid gap-3 lg:grid-cols-[1fr_auto_1fr_1fr_1fr_1.2fr_auto] items-stretch [&>*]:min-w-0">
        <AirportField label="From" value={from} onChange={setFrom} testid="from-airport" />
        <button onClick={swap} data-testid="swap-airports-btn" aria-label="Swap"
          className="self-center justify-self-center h-10 w-10 rounded-full border border-slate-200 bg-white grid place-items-center text-[#EC5B24] hover:bg-orange-50 lg:-mx-5 z-10 transition-transform duration-300"
          style={{ transform: `rotate(${spin ? 180 : 0}deg)` }}>
          <ArrowLeftRight className="h-4 w-4" />
        </button>
        <AirportField label="To" value={to} onChange={setTo} testid="to-airport" />
        <DateField label="Departure" value={depart} onChange={(d) => { setDepart(d); if (ret && ret < d) setRet(""); }} min={today} testid="depart-date" />
        <DateField label="Return" value={ret} placeholder="Add return for bigger savings" min={depart} testid="return-date"
          onChange={(d) => { setRet(d); setTrip("round"); }} onClear={() => { setRet(""); setTrip("oneway"); }} />
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="travellers-trigger" className="text-left w-full rounded-xl border border-slate-200 hover:border-[#EC5B24] px-4 py-3 bg-white transition-colors">
              <div className="text-xs font-medium text-slate-500">Travellers & Class</div>
              <div className="font-heading text-lg font-bold text-slate-900">{total} Traveller{total > 1 ? "s" : ""}</div>
              <div className="text-xs text-slate-500">{CABINS.find((c) => c.id === cabin)?.label}</div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <Counter label="Adults" sub="12 yrs or above" value={pax.adults} min={1} max={9} testid="pax-adults" set={(v) => setPax({ ...pax, adults: v, infants: Math.min(pax.infants, v) })} />
            <Counter label="Children" sub="2 - 12 yrs" value={pax.children} min={0} max={6} testid="pax-children" set={(v) => setPax({ ...pax, children: v })} />
            <Counter label="Infants" sub="Under 2 yrs" value={pax.infants} min={0} max={pax.adults} testid="pax-infants" set={(v) => setPax({ ...pax, infants: v })} />
            <div className="text-sm font-semibold mt-3 mb-2">Class</div>
            <div className="flex flex-wrap gap-2">
              {CABINS.map((c) => (
                <button key={c.id} data-testid={`cabin-${c.id}`} onClick={() => setCabin(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${cabin === c.id ? "bg-orange-50 border-[#EC5B24] text-[#EC5B24]" : "border-slate-200 text-slate-600"}`}>{c.label}</button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <button onClick={submit} data-testid="search-flights-btn"
          className="rounded-xl bg-[#EC5B24] hover:bg-[#D84A15] active:scale-[0.98] text-white font-heading font-bold text-lg px-8 py-3 flex items-center justify-center gap-2 transition-[background-color,transform]">
          <Search className="h-5 w-5" /> Search
        </button>
      </div>
    </div>
  );
};
